import React, { useEffect, useState } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import { buildApiUrl, useBaseUrl } from '../../../utils/utils';
import localforage from 'localforage';
import Swal from 'sweetalert2';

interface NoteTemplate {
    id: number;
    name: string;
    category: string;
    type: string;
    specialty: string;
    content: string;
    is_shared: boolean;
    shared_by: string | null;
    usage_count: number;
    last_used: string;
    mine: boolean;
}

const NOTE_TYPES = ['SOAP', 'H&P', 'Progress', 'Discharge', 'Referral', 'Procedure', 'Custom'];

const ClinicalTemplates: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'my-templates' | 'shared' | 'create'>('my-templates');
    const [searchQuery, setSearchQuery] = useState('');

    const [mine, setMine] = useState<NoteTemplate[]>([]);
    const [shared, setShared] = useState<NoteTemplate[]>([]);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState({ name: '', note_type: 'SOAP', specialty: '', category: '', content: '', is_shared: false });
    const [submitting, setSubmitting] = useState(false);
    const [viewing, setViewing] = useState<NoteTemplate | null>(null);

    useEffect(() => {
        document.title = "Clinical Notes & Templates - Uzima Healthcare";
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, []);

    const fetchTemplates = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `ehr/clinical-templates?clinic_id=${clinicId}`));
            const data = await res.json();
            if (data.status === 'success') {
                setMine(data.data.mine || []);
                setShared(data.data.shared || []);
            }
        } catch { /* leave lists empty on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTemplates(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    const typeBadge = (type: string) => {
        const colors: Record<string, string> = { SOAP: 'primary', 'H&P': 'info', Progress: 'success', Discharge: 'warning', Referral: 'secondary', Procedure: 'danger', Custom: 'dark' };
        return <span className={`badge bg-${colors[type] || 'secondary'}-subtle text-${colors[type] || 'secondary'}`}>{type}</span>;
    };

    const submitTemplate = async () => {
        if (!form.name.trim() || !form.content.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Name and content are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `ehr/clinical-templates`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, staff_id: staffId, ...form }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Template Saved', timer: 1500, showConfirmButton: false });
                setForm({ name: '', note_type: 'SOAP', specialty: '', category: '', content: '', is_shared: false });
                setActiveTab('my-templates');
                fetchTemplates();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to save template.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while saving template.' });
        } finally {
            setSubmitting(false);
        }
    };

    const useThisTemplate = async (t: NoteTemplate) => {
        try {
            await fetch(buildApiUrl(baseUrl, `ehr/clinical-templates/${t.id}/use`), { method: 'POST' });
            fetchTemplates();
        } catch { /* usage counter is non-critical */ }
        setViewing(t);
    };

    const list = activeTab === 'shared' ? shared : mine;
    const filtered = list.filter(t =>
        !searchQuery.trim() ||
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.specialty.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Clinical Notes & Templates</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/dashboard/clinician">Clinician</a></li>
                                            <li className="breadcrumb-item active">Clinical Notes & Templates</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            {[
                                { label: 'My Templates', value: String(mine.length), icon: 'ri-file-list-3-line', color: 'primary' },
                                { label: 'Network Shared', value: String(shared.length), icon: 'ri-share-line', color: 'info' },
                                { label: 'Total Uses (Mine)', value: String(mine.reduce((s, t) => s + t.usage_count, 0)), icon: 'ri-edit-line', color: 'success' },
                            ].map((stat, i) => (
                                <div className="col-xl-4 col-md-6" key={i}>
                                    <div className="card card-animate">
                                        <div className="card-body">
                                            <p className="text-uppercase fw-medium text-muted mb-0">{stat.label}</p>
                                            <div className="d-flex align-items-end justify-content-between mt-4">
                                                <h4 className="fs-22 fw-semibold mb-0">{stat.value}</h4>
                                                <div className="avatar-sm flex-shrink-0">
                                                    <span className={`avatar-title bg-${stat.color}-subtle rounded fs-3`}>
                                                        <i className={`${stat.icon} text-${stat.color}`}></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <ul className="nav nav-tabs-custom card-header-tabs border-bottom-0">
                                    {[
                                        { key: 'my-templates', label: 'My Templates', icon: 'ri-file-list-3-line' },
                                        { key: 'shared', label: 'Network Templates', icon: 'ri-share-line' },
                                        { key: 'create', label: '+ New Template', icon: 'ri-add-line' },
                                    ].map(tab => (
                                        <li className="nav-item" key={tab.key}>
                                            <a className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                               href="#" onClick={(e) => { e.preventDefault(); setActiveTab(tab.key as any); }}>
                                                <i className={`${tab.icon} me-1`}></i> {tab.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="card-body">
                                {(activeTab === 'my-templates' || activeTab === 'shared') && (
                                    <>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <input type="text" className="form-control" placeholder="Search templates..."
                                                       value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                            </div>
                                        </div>
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                            </div>
                                        ) : filtered.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-file-list-3-line" style={{ fontSize: '2.5rem' }}></i>
                                                <h5 className="mt-3">No templates {activeTab === 'shared' ? 'shared by other clinics' : 'yet'}</h5>
                                                <p className="text-muted">
                                                    {activeTab === 'shared'
                                                        ? 'When other clinics on the network share a template, it will appear here.'
                                                        : 'Create your first note template to speed up documentation.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                {filtered.map(template => (
                                                    <div className="col-lg-4 col-md-6" key={template.id}>
                                                        <div className="card border shadow-none" style={{ cursor: 'pointer' }} onClick={() => useThisTemplate(template)}>
                                                            <div className="card-body">
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <h6 className="mb-0">{template.name}</h6>
                                                                    {typeBadge(template.type)}
                                                                </div>
                                                                <p className="text-muted small mb-2">{template.category || '—'} · {template.specialty || '—'}</p>
                                                                {!template.mine && template.shared_by && (
                                                                    <small className="text-info d-block mb-2"><i className="ri-share-line me-1"></i>Shared by {template.shared_by}</small>
                                                                )}
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <small className="text-muted">Used {template.usage_count}x</small>
                                                                    <button className="btn btn-sm btn-soft-primary" onClick={(e) => { e.stopPropagation(); useThisTemplate(template); }}>
                                                                        <i className="ri-eye-line"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}

                                {activeTab === 'create' && (
                                    <div className="row">
                                        <div className="col-lg-8 mx-auto">
                                            <h5 className="mb-4">Create New Template</h5>
                                            <div className="mb-3">
                                                <label className="form-label">Template Name</label>
                                                <input type="text" className="form-control" placeholder="e.g. Hypertension Follow-up"
                                                       value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
                                            </div>
                                            <div className="row g-3 mb-3">
                                                <div className="col-md-4">
                                                    <label className="form-label">Type</label>
                                                    <select className="form-select" value={form.note_type} onChange={(e) => setForm(f => ({ ...f, note_type: e.target.value }))}>
                                                        {NOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Specialty</label>
                                                    <input type="text" className="form-control" placeholder="e.g. Cardiology"
                                                           value={form.specialty} onChange={(e) => setForm(f => ({ ...f, specialty: e.target.value }))} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label">Category</label>
                                                    <input type="text" className="form-control" placeholder="e.g. Primary Care"
                                                           value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} />
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Template Content</label>
                                                <textarea className="form-control" rows={10} placeholder="Write your template structure...&#10;&#10;Use [brackets] for fillable fields, e.g.:&#10;Chief Complaint: [CC]&#10;History: [HPI]"
                                                          value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}></textarea>
                                            </div>
                                            <div className="form-check mb-3">
                                                <input className="form-check-input" type="checkbox" id="shareTemplate"
                                                       checked={form.is_shared} onChange={(e) => setForm(f => ({ ...f, is_shared: e.target.checked }))} />
                                                <label className="form-check-label" htmlFor="shareTemplate">Share this template with the Uzima network</label>
                                            </div>
                                            <button className="btn btn-primary" disabled={submitting} onClick={submitTemplate}>
                                                <i className="ri-save-line me-1"></i> {submitting ? 'Saving...' : 'Save Template'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.name}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{viewing.content}</pre>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClinicalTemplates;
